/**
 * TBB native Memory Wall — PDP chat-room widget.
 *
 * NATIVE CUTOVER (scope P3): the ADAPTER below speaks the TBB social engine
 * (/v1/social/* on the rankings worker). Identity is the signed tbb_social
 * cookie — buyers mint it via the EXISTING tbb-collector app proxy
 * (/apps/tbb-collector/v1/social/token → worker /proxy/collector/v1/social/token),
 * sellers arrive with it already set by the portal (Domain=.throwbackbuys.com).
 * Every engine call sends credentials so the cookie rides.
 *
 * Moderation is soft: POST /comments returns status "published" | "pending"
 * plus a message we render VERBATIM. "pending" covers held AND rejected —
 * the UI never distinguishes the two or reveals a reason.
 *
 * Behavior change from the Commentify build: guests can no longer post
 * anonymously (the engine requires a signed identity). Guests get a
 * sign-in prompt instead of the composer.
 *
 * CONVENTION (the P4 Commentify importer must match): product comments are
 * keyed target_type="product", target_id=<product handle>.
 */
(function () {
  'use strict';

  var root = document.getElementById('tbbPdpWall');
  if (!root) return;

  var PATH = root.getAttribute('data-path');
  var HANDLE = (PATH.split('/products/')[1] || '').replace(/[?#].*$/, '');
  var ENGINE = 'https://rankings.throwbackbuys.com/v1/social';
  var MINT = '/apps/tbb-collector/v1/social/token';
  var LOGIN = '/customer_authentication/login?return_to=' + encodeURIComponent(PATH);
  var NICK_COLORS = ['#FE8AED', '#8de0f4', '#2ECFCF', '#c39bff', '#6bff8e'];
  var SEAL_PATHS = '<path d="M12 1l2.6 2.2 3.4-.4 1.2 3.2 3 1.6-1 3.3 1 3.3-3 1.6-1.2 3.2-3.4-.4L12 21l-2.6-2.4-3.4.4-1.2-3.2-3-1.6 1-3.3-1-3.3 3-1.6 1.2-3.2 3.4.4z"/><path d="M10.5 15.2l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z" fill="#0a1220"/>';
  // DS 3.17 role pill: the vetted check plus the role word, one component on
  // every surface (styled by .tbb-role in tbb-reskin.css). Nothing for buyers.
  function rolePill(authorType) {
    if (authorType === 'creator') return '<span class="tbb-role tbb-role--creator" title="Creator"><svg viewBox="0 0 24 24" aria-hidden="true">' + SEAL_PATHS + '</svg>Creator</span>';
    if (authorType === 'seller') return '<span class="tbb-role" title="Vetted seller"><svg viewBox="0 0 24 24" aria-hidden="true">' + SEAL_PATHS + '</svg>Seller</span>';
    return '';
  }

  /* ── ADAPTER (native /v1/social/*) ─────────────────────────── */
  var adapter = {
    list: async function () {
      // No credentials on purpose. A cookie makes Cloudflare skip the edge
      // cache the engine sets, and this is the heaviest call on the PDP.
      // Public comment reads carry no viewer state; dap state comes from a
      // separate call and that one keeps its cookie. (2026-07-29)
      var res = await TBBFetch(ENGINE + '/comments?target_type=product&target_id=' + encodeURIComponent(HANDLE) + '&limit=200', { timeout: 10000 });
      return res.json(); // { success, comments: [{id, parentId, authorType, author, body, pinned, createdAt}] }
    },
    post: async function (body, parentId) {
      var res = await TBBFetch(ENGINE + '/comments', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_type: 'product', target_id: HANDLE, parent_id: parentId || null, body: body })
      });
      return { status: res.status, json: await res.json().catch(function () { return null; }) };
    },
    dapState: async function (commentId) {
      var res = await TBBFetch(ENGINE + '/daps?target_type=comment&target_id=' + encodeURIComponent(commentId), { credentials: 'include' });
      return res.json(); // { success, count, dapped }
    },
    dapToggle: async function (commentId) {
      var res = await TBBFetch(ENGINE + '/daps', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_type: 'comment', target_id: commentId })
      });
      return { status: res.status, json: await res.json().catch(function () { return null; }) };
    },
    mint: async function () {
      // TWO HOPS, and the second one is the whole point. Shopify's app proxy
      // strips Set-Cookie on the way back (public routes), so the mint can only
      // hand us a single-use, 60-second CODE in the body — bodies pass through
      // fine. We redeem that code directly against rankings.throwbackbuys.com
      // with no proxy in the path, and THAT response sets the real HttpOnly
      // tbb_social cookie. Guests get { authenticated: false } and no code.
      // Sellers never come through here; the portal already set their cookie.
      // (engine lane, 2026-07-29)
      var res = await TBBFetch(MINT, { credentials: 'include' });
      if (!res.ok) throw new Error('mint ' + res.status);
      var data = await res.json(); // { success, authenticated, name?, code? }
      if (data && data.authenticated && data.code) {
        var r2 = await TBBFetch(ENGINE + '/session', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: data.code })
        });
        if (!r2.ok) throw new Error('session ' + r2.status);
      }
      return data;
    },
    whoami: async function () {
      var res = await TBBFetch(ENGINE + '/whoami', { credentials: 'include' });
      return res.json(); // { success, authenticated, authorType?, name?, isAdmin? }
    },
    del: async function (commentId) {
      // Admin soft-delete (state=rejected). Cookie-authed, isAdmin only.
      var res = await TBBFetch(ENGINE + '/comments/delete', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: commentId })
      });
      return { status: res.status, json: await res.json().catch(function () { return null; }) };
    }
  };

  /* ── State ─────────────────────────────────────────────────── */
  var comments = [];      // flat: {id, parentHex, name, authorType, body, created, pinned, count, dapped, pending}
  var replyTarget = null; // comment id being replied to
  var CUSTOMER = window.TBB_PW_CUSTOMER || null;
  var IDENTITY = null;    // whoami result: {authenticated, authorType, name}

  init();

  async function init() {
    renderComposer();
    bindScrollHandoff();
    resolveIdentity(); // mint + whoami: upgrades the composer for sellers
    try {
      var data = await adapter.list();
      comments = normalize(data);
    } catch (e) {
      console.error('TBB wall: list failed', e);
      document.getElementById('pwLog').innerHTML =
        '<div class="pw-empty">The wall is not answering right now. Refresh to retry.</div>';
      return;
    }
    renderLog();
    loadDaps();
  }

  /* ── Scroll hand-off ──────────────────────────────
     The log is a nested scroller sitting in the middle of a long PDP.
     Two failure modes, and the fix has to kill one without causing the
     other:
       1. Wheel latching — Chrome pins a trackpad gesture to whichever
          scroller it started over, so on reaching the top or bottom of
          the log the page stops dead until you lift off. Reads as frozen.
          overscroll-behavior alone does NOT fix this (verified 2026-07-27).
       2. Double-scroll — if the log scrolls AND we push the page, one
          flick moves two things at once.

     WHEEL: while the log can still travel in the gesture direction we do
     nothing and let it own the gesture. The moment it cannot, we cancel
     the event and move the page by exactly that delta. Wheel events stay
     cancelable for a whole gesture, so this works.

     TOUCH: the same trick cannot work, and shipping it made things worse
     (2026-07-28). Two reasons:
       a. Once a finger has started scrolling the log, Safari and Chrome
          both fire every later touchmove with cancelable === false. The
          hand-off hit `if (!e.cancelable) return` and bailed silently, so
          the page froze until lift-off — the exact bug it was added to fix.
       b. preventDefault + window.scrollBy is a 1:1 finger drag with no
          inertia, so even when it did fire the page stopped dead on lift
          while the rest of the PDP flicked normally.
     What actually strands the thumb is the scroller sitting EXACTLY on an
     edge when the gesture begins — that is when the browser latches to it
     and refuses to chain (verified on a 360px Android preset, 2026-07-27).
     renderLog parks the log at its bottom edge, so that is the common case,
     not the rare one. So on touchstart we nudge it 1px off the edge and
     then stay out of the way: the browser owns every touch scroll, chains
     natively via overscroll-behavior-y: auto, and keeps its momentum.
     (LeMarc, 2026-07-28) */
  function bindScrollHandoff() {
    var log = document.getElementById('pwLog');
    if (!log) return;

    // Can the log still travel in this direction? dy < 0 = toward the top.
    // 1px tolerance: scrollTop is fractional on HiDPI, so an exact
    // ===0 / ===slack test never fires at the ends.
    function canTravel(dy) {
      var slack = log.scrollHeight - log.clientHeight;
      return slack > 1 && (dy < 0 ? log.scrollTop > 1 : log.scrollTop < slack - 1);
    }
    function handOff(e, dy) {
      if (!e.cancelable) return; // the browser already committed this gesture
      e.preventDefault();
      window.scrollBy({ top: dy, left: 0, behavior: 'instant' });
    }

    log.addEventListener('wheel', function (e) {
      if (e.ctrlKey) return; // pinch-zoom, not a scroll
      var dy = e.deltaY;
      if (!dy) return;
      // deltaMode 1 = lines, 2 = pages -- normalize to px before handing off.
      if (e.deltaMode === 1) dy *= 16;
      else if (e.deltaMode === 2) dy *= log.clientHeight;
      if (canTravel(dy)) return; // the log owns this gesture -- hands off
      handOff(e, dy);
    }, { passive: false });

    // Touch: never cancel, never emulate. Only keep the log off its exact
    // edges at gesture start, which is the condition that makes the browser
    // latch onto it. Passive on purpose -- we never preventDefault here.
    log.addEventListener('touchstart', function () {
      var slack = log.scrollHeight - log.clientHeight;
      if (slack <= 1) return;                  // not scrollable -- page owns it
      if (log.scrollTop < 1) log.scrollTop = 1;
      else if (log.scrollTop > slack - 1) log.scrollTop = slack - 1;
    }, { passive: true });
  }

  function normalize(data) {
    if (!data || !data.success || !Array.isArray(data.comments)) return [];
    // Server order kept: pinned first, then oldest -> newest.
    return data.comments.map(function (c) {
      return {
        id: c.id,
        parentHex: c.parentId || 'root',
        name: c.author || 'Anonymous',
        authorType: c.authorType || 'customer',
        curator: c.curator || null,   // seller identity block; null for imported/customer
        body: c.body || '',
        created: c.createdAt,
        pinned: !!c.pinned,
        count: null,   // dap count, hydrated by loadDaps()
        dapped: false,
        pending: false
      };
    });
  }

  /* ── Render ────────────────────────────────────────────────── */

  function renderLog() {
    var log = document.getElementById('pwLog');
    var roots = comments.filter(function (c) { return c.parentHex === 'root'; });
    renderStrip();
    // A wall with nothing (or one memory) on it was still painting the full
    // 320px window, so most of the room was dead black. `pw-sparse` lets the
    // log shrink to its content; the 320px stays as a cap the moment there is
    // real scrollback to cap. (LeMarc, 2026-07-29)
    log.classList.toggle('pw-sparse', roots.length <= 1);
    if (!roots.length) {
      log.innerHTML = '<div class="pw-empty">No memories on this one yet. You could be first.</div>';
      return;
    }
    log.innerHTML = '';
    // Linear chatroom log: each memory line, its replies right under it as arrow lines.
    roots.forEach(function (c) {
      log.appendChild(line(c, false));
      comments.filter(function (r) { return r.parentHex === c.id; })
        .forEach(function (r) { log.appendChild(line(r, true, c.id)); });
    });
    log.scrollTop = log.scrollHeight; // sit at the newest line, chatroom style
  }

  // Counts read as copy, so they get real plurals. null/unknown falls back to
  // the plural form ("· Daps") — never "· Dap". (LeMarc, 2026-07-27)
  function plural(n, one, many) { return n === 1 ? one : many; }

  function renderStrip() {
    var roots = comments.filter(function (c) { return c.parentHex === 'root'; });
    var people = {};
    comments.forEach(function (c) { people[c.name] = 1; });
    var daps = comments.reduce(function (s, c) { return s + (c.count || 0); }, 0);
    var nPeople = Object.keys(people).length;
    document.getElementById('pwStrip').innerHTML =
      '*** <span class="pw-topic">topic: ' + escapeHtml(root.getAttribute('data-product-title')) + '</span>' +
      ' — <span class="pw-dot">●</span> ' + nPeople + ' ' + plural(nPeople, 'archivist', 'archivists') + ' · ' +
      roots.length + ' ' + plural(roots.length, 'memory', 'memories') + ' · ' +
      daps + ' ' + plural(daps, 'dap', 'daps');
  }

  function bodyHtml(c) {
    // Native comments are stored as plain text; imported Commentify bodies may carry HTML.
    return c.authorType === 'imported' ? sanitizeHtml(c.body) : escapeHtml(c.body);
  }

  // One identity treatment, same source of truth as the Memory Wall page:
  // when the engine sends a `curator` block the name is the curator's, linked
  // to their Time Capsule and sealed. Bare nick + nickColor is the fallback for
  // curator: null (imported and customer rows).
  // The avatar / location / stats that the wall page's memory CARD renders are
  // deliberately NOT here — this widget is a one-line chat log, and hanging a
  // stats block off a chat line breaks the room. Name + seal + link is the
  // whole identity a line can carry. (2026-07-29)
  function nickHtml(c) {
    var cur = c.curator;
    if (cur) {
      return '<a class="pw-nick pw-cur-link" href="' + escapeHtml(cur.profileUrl || '#') +
        '" style="color:' + nickColor(cur.name || cur.handle) + '" title="' +
        escapeHtml(cur.location ? cur.name + ' — ' + cur.location : (cur.name || cur.handle)) +
        '">' + escapeHtml(cur.name || cur.handle) + '</a>' + (rolePill(c.authorType) || rolePill('seller'));
    }
    return '<span class="pw-nick" style="color:' + nickColor(c.name) + '">' + escapeHtml(c.name) + '</span>' +
      rolePill(c.authorType);
  }

  // DS > Memory Wall chat room > Message line: 36px DiceBear `glass` seeded by
  // the screen name — the same open-source set the wall page uses, so the same
  // person renders the same face on both surfaces.
  function avatarUrl(c) {
    var cur = c.curator;
    if (cur && cur.avatar) return cur.avatar;
    return 'https://api.dicebear.com/9.x/glass/svg?seed=' +
      encodeURIComponent(c.name || 'archivist');
  }

  function line(c, isReply, rootId) {
    var el = document.createElement('div');
    el.className = 'pw-line' + (isReply ? ' pw-is-reply' : '');
    // DESIGN_SYSTEM.md > "Memory Wall chat room" > Message line, applied
    // literally instead of re-invented: avatar column + content column, meta
    // row (name, seal, age, and the moderator × at the right edge), body, then
    // the action row. A reply is the SAME markup — it only picks up the
    // indent rule and a one-notch type step from CSS. Previously a reply was a
    // different shape from its parent (a ↳ glyph, no meta row, the admin ×
    // stranded on its own line), which is what read as inconsistent.
    // (LeMarc, 2026-07-29)
    var adminX = (IDENTITY && IDENTITY.isAdmin) ?
      '<button class="pw-modx" data-id="' + escapeHtml(c.id) + '" title="Delete (moderator)">&times;</button>' : '';
    // Every line is dappable and replyable — a reply is a memory too, and the
    // wall page has always dapped statuses and memories alike. Threading stays
    // ONE level deep: replying to a reply targets the root memory, so the log
    // never grows a third indent. (LeMarc, 2026-07-29)
    var replyTargetId = isReply ? (rootId || c.id) : c.id;
    var actsHtml =
      '<div class="pw-acts">' +
        '<button class="pw-dap' + (c.dapped ? ' dapped' : '') + '" data-id="' + escapeHtml(c.id) + '" title="' + (c.dapped ? 'Remove Daps' : 'Give Daps') + '">🤜 <span class="pw-dap-n">' + (c.count == null ? '·' : c.count) + '</span> <span class="pw-dap-w">' + plural(c.count, 'Dap', 'Daps') + '</span></button>' +
        '<button class="pw-replybtn" data-id="' + escapeHtml(replyTargetId) + '" data-name="' + escapeHtml(c.name) + '">Reply</button>' +
      '</div>';
    el.innerHTML =
      '<img class="pw-avatar" src="' + escapeHtml(avatarUrl(c)) + '" alt="" loading="lazy">' +
      '<div class="pw-main">' +
        '<div class="pw-meta">' +
          (c.pinned ? '<span class="pw-pin" title="Pinned by TBB">📌</span>' : '') +
          nickHtml(c) +
          '<span class="pw-ts">' + timeAgo(c.created) + '</span>' +
          (c.pending ? '<span class="pw-pending">pending approval</span>' : '') +
          adminX +
        '</div>' +
        '<div class="pw-body">' + bodyHtml(c) + '</div>' +
        actsHtml +
      '</div>';
    var dapBtn = el.querySelector('.pw-dap');
    var replyBtn = el.querySelector('.pw-replybtn');
    var modBtn = el.querySelector('.pw-modx');
    if (dapBtn) dapBtn.addEventListener('click', onDap);
    if (replyBtn) replyBtn.addEventListener('click', onReplyClick);
    if (modBtn) modBtn.addEventListener('click', onModX);
    return el;
  }

  async function onModX() {
    var btn = this;
    if (btn.disabled) return;
    // Two-tap confirm — no browser dialogs.
    if (!btn.classList.contains('armed')) {
      btn.classList.add('armed');
      btn.textContent = 'del?';
      setTimeout(function () { btn.classList.remove('armed'); btn.textContent = 'x'; }, 3000);
      return;
    }
    btn.disabled = true;
    var id = btn.getAttribute('data-id');
    try {
      var r = await adapter.del(id);
      if (r.json && r.json.success) {
        comments = comments.filter(function (x) { return x.id !== id && x.parentHex !== id; });
        renderLog();
        toast('Deleted.');
      } else if (r.status === 403) {
        toast('Moderator only.');
      } else {
        toast('Could not delete that one.');
      }
    } catch (e) {
      console.error('TBB wall: delete failed', e);
      toast('Could not delete that one.');
    } finally { btn.disabled = false; }
  }

  /* ── Composer ─────────────────────────────────────────────── */

  function renderComposer() {
    var box = document.getElementById('pwComposer');
    var isSeller = IDENTITY && IDENTITY.authorType === 'seller';
    var isCreator = IDENTITY && IDENTITY.authorType === 'creator';
    var isPortalMember = isSeller || isCreator;
    if (!CUSTOMER && !isPortalMember) {
      box.innerHTML = '<span class="pw-signin">The archive is read-only for lurkers. ' +
        '<a href="' + LOGIN + '">Sign in</a> to drop a memory.</span>';
      return;
    }
    var name = (IDENTITY && IDENTITY.name) || (CUSTOMER && CUSTOMER.name) || 'you';
    // Name first, role pill underneath (LeMarc 2026-10-08): side by side, the pill
    // and name crowded each other in the composer's narrow left column.
    box.innerHTML = '<span class="pw-composer-id"><span class="pw-composer-name">' + escapeHtml(name) + '</span>' + rolePill(IDENTITY && IDENTITY.authorType) + '</span>' +
      '<input class="pw-input" id="pwInput" type="text" maxlength="1500" placeholder="This one time, at band camp...">' +
      '<button class="pw-send" id="pwSend">Send</button>';
    var sendBtn = document.getElementById('pwSend');
    var inputEl = document.getElementById('pwInput');
    sendBtn.addEventListener('click', submit);
    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });
    // SEND fills solid only when there is something to send — same idiom as
    // .pw-dap.dapped, so the fill reads as state rather than decoration.
    // (LeMarc, 2026-07-29)
    inputEl.addEventListener('input', function () {
      sendBtn.classList.toggle('ready', this.value.trim().length > 0);
    });
  }

  function onReplyClick() {
    replyTarget = this.getAttribute('data-id');
    var bar = document.getElementById('pwReplying');
    bar.style.display = 'block';
    bar.innerHTML = 'replying to ' + escapeHtml(this.getAttribute('data-name')) +
      ' <button type="button" id="pwCancelReply">[cancel]</button>';
    document.getElementById('pwCancelReply').addEventListener('click', function () {
      replyTarget = null;
      bar.style.display = 'none';
    });
    var input = document.getElementById('pwInput');
    if (input) input.focus();
  }

  var submitting = false;
  async function submit() {
    if (submitting) return;
    var input = document.getElementById('pwInput');
    var text = (input.value || '').trim();
    if (!text) return;
    submitting = true;
    var sendBtn = document.getElementById('pwSend');
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending...'; // visible busy state
    try {
      var r = await adapter.post(text, replyTarget);
      if (r.status === 401 && await pwRecover401()) r = await adapter.post(text, replyTarget);
      if (r.status === 401) {
        toast(CUSTOMER ? 'Could not verify your session. Give the page a refresh.' : 'Sign in to drop a memory.');
      } else if (r.status === 429) {
        toast('The wall needs a breather. Try again in a minute.');
      } else if (r.json && r.json.success) {
        comments.push({
          id: r.json.id,
          parentHex: replyTarget || 'root',
          name: (IDENTITY && IDENTITY.name) || (CUSTOMER && CUSTOMER.name) || 'you',
          authorType: (IDENTITY && IDENTITY.authorType) || 'customer',
          body: text,
          created: new Date().toISOString(),
          pinned: false,
          count: 0,
          dapped: false,
          pending: r.json.status === 'pending'
        });
        input.value = '';
        replyTarget = null;
        document.getElementById('pwReplying').style.display = 'none';
        renderLog();
        if (r.json.message) toast(r.json.message); // server copy, rendered verbatim
      } else {
        toast('The wall did not take that one. Try again.');
      }
    } catch (e) {
      console.error('TBB wall: post failed', e);
      toast('The wall did not take that one. Try again.');
    } finally {
      submitting = false;
      sendBtn.disabled = false;
      sendBtn.textContent = 'Send';
      sendBtn.classList.toggle('ready', !!(input.value || '').trim());
    }
  }

  /* ── Daps (target_type=comment, one per identity, toggle) ─── */

  function byId(id) {
    for (var i = 0; i < comments.length; i++) if (comments[i].id === id) return comments[i];
    return null;
  }

  async function loadDaps() {
    var roots = comments.filter(function (c) { return c.parentHex === 'root'; });
    await Promise.all(roots.map(async function (c) {
      try {
        var d = await adapter.dapState(c.id);
        if (d && d.success) { c.count = d.count || 0; c.dapped = !!d.dapped; }
      } catch (e) { /* non-fatal */ }
    }));
    paintDaps();
    renderStrip();
  }

  function paintDaps() {
    root.querySelectorAll('.pw-dap').forEach(function (btn) {
      var c = byId(btn.getAttribute('data-id'));
      if (!c || c.count == null) return;
      btn.querySelector('.pw-dap-n').textContent = c.count;
      btn.querySelector('.pw-dap-w').textContent = plural(c.count, 'Dap', 'Daps');
      btn.classList.toggle('dapped', c.dapped);
      btn.title = c.dapped ? 'Remove Daps' : 'Give Daps';
    });
  }

  async function onDap() {
    var btn = this;
    var id = btn.getAttribute('data-id');
    var c = byId(id);
    if (!c || btn.disabled) return;
    btn.disabled = true;
    var was = !!c.dapped;
    var base = c.count || 0;
    c.dapped = !was;
    c.count = was ? Math.max(0, base - 1) : base + 1;
    paintDaps();
    try {
      var r = await adapter.dapToggle(id);
      if (r.status === 401 && await pwRecover401()) r = await adapter.dapToggle(id);
      if (r.json && r.json.success) {
        c.dapped = !!r.json.dapped;
        c.count = r.json.count;
      } else {
        c.dapped = was; c.count = base;
        if (r.status === 401) toast('Could not verify your session. Give the page a refresh.');
        else if (r.status === 429) toast('Easy on the daps. Give it a minute.');
      }
    } catch (e) {
      c.dapped = was; c.count = base;
    } finally {
      btn.disabled = false;
      paintDaps();
      renderStrip();
    }
  }

  /* ── Identity (tbb_social cookie) ─────────────────────────── */

  var mintPromise = null;
  function mintCookie() {
    // One call per page load. The engine sets the signed tbb_social cookie
    // for logged-in Shopify customers. Rides the existing tbb-collector app
    // proxy (same app, same HMAC secret) — fails soft until the engine routes
    // /proxy/collector/v1/social/token → handleSocialMint. Sellers already
    // carry the cookie from portal login.
    if (mintPromise) return mintPromise;
    mintPromise = adapter.mint().catch(function (e) {
      console.warn('TBB wall: token mint unavailable', e && e.message);
      return null;
    });
    return mintPromise;
  }

  /* A 401 on a write is NOT proof of a guest. A signed-in Shopify customer hits
     it too whenever the tbb_social cookie was never minted or has expired — and
     for that user /customer_authentication/login shows no login screen at all,
     it bounces straight back to the top of the PDP. Visually identical to a
     refresh, so the tap reads as broken. (LeMarc, confirmed on the wall page
     2026-07-29.) So: only a true guest gets sent to login. Everyone else gets
     one silent re-mint and one retry; a second 401 says so out loud instead of
     navigating. Mirrors mw2Recover401 in page.memory-wall-v2.liquid. */
  var reminted = false;
  async function pwRecover401() {
    if (!CUSTOMER) { window.location.href = LOGIN; return false; }
    if (reminted) return false;
    reminted = true;
    mintPromise = null;  // drop the cached page-load mint and ask for a fresh cookie
    await mintCookie();
    return true;
  }

  async function resolveIdentity() {
    // whoami reads the tbb_social cookie server-side: sellers and Creators
    // logged into Portal get the composer even when they are not a Shopify
    // customer. The vetted seller seal remains seller-only.
    try {
      if (CUSTOMER) await mintCookie(); // ensure the cookie exists first
      var w = await adapter.whoami();
      if (w && w.success && w.authenticated) {
        IDENTITY = w;
        var input = document.getElementById('pwInput');
        if (!input || !input.value) renderComposer(); // never clobber a draft
        if (w.isAdmin) renderLog(); // repaint with moderator x actions
      }
    } catch (e) { /* soft: guest prompt stands */ }
  }

  /* ── Utilities ────────────────────────────────────────────── */

  function toast(msg) {
    var existing = document.querySelector('.mw2-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.className = 'mw2-toast';
    t.style.cssText = 'position:fixed;left:50%;bottom:84px;transform:translateX(-50%);z-index:40;max-width:min(420px,90vw);padding:10px 14px;font-family:ui-monospace,monospace;font-size:12px;color:#fff;text-align:center;background:rgb(15,15,30);border:2px solid #8de0f4;border-radius:6px;box-shadow:0 0 16px rgba(141,224,244,0.3);';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(function () { t.remove(); }, 3000);
  }
  function nickColor(name) {
    var h = 0; name = name || '?';
    for (var i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0;
    return NICK_COLORS[Math.abs(h) % NICK_COLORS.length];
  }
  function timeAgo(dateStr) {
    if (!dateStr) return '';
    var s = Math.floor((new Date() - new Date(dateStr)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s / 60) + 'm ago';
    if (s < 86400) return Math.floor(s / 3600) + 'h ago';
    if (s < 2592000) return Math.floor(s / 86400) + 'd ago';
    if (s < 31536000) return Math.floor(s / 2592000) + 'mo ago';
    return Math.floor(s / 31536000) + 'y ago';
  }
  function escapeHtml(str) {
    if (str == null) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
  function sanitizeHtml(html) {
    if (!html) return '';
    var SAFE_TAGS = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'span', 'hr', 'del']);
    var doc = new DOMParser().parseFromString(html, 'text/html');
    function walk(parent) {
      var out = document.createDocumentFragment();
      parent.childNodes.forEach(function (node) {
        if (node.nodeType === 3) { out.appendChild(document.createTextNode(node.textContent)); return; }
        if (node.nodeType !== 1) return;
        var tag = node.tagName.toLowerCase();
        if (!SAFE_TAGS.has(tag)) { out.appendChild(walk(node)); return; }
        var el = document.createElement(tag);
        if (tag === 'a') {
          var href = (node.getAttribute('href') || '').trim();
          if (href && href.toLowerCase().indexOf('javascript:') !== 0 && href.toLowerCase().indexOf('data:') !== 0) {
            el.setAttribute('href', href);
          }
          el.setAttribute('rel', 'noopener noreferrer');
        }
        el.appendChild(walk(node));
        out.appendChild(el);
      });
      return out;
    }
    var div = document.createElement('div');
    div.appendChild(walk(doc.body));
    return div.innerHTML;
  }
})();
